<script setup lang="ts">
  import { computed } from 'vue';
  import type { Deliverable, ProfileMetric } from './CreatorSocialMetrics.vue';
  import { formatNumber } from '@/utils/strings';

  interface Props {
    deliverable: Deliverable;
  }

  const props = defineProps<Props>();

  const deliverablePlatformIcon = computed(() => {
    if (props.deliverable.platform.toLowerCase() === 'twitter') {
      return '/src/assets/img/social/xtwitter.png';
    } else if (props.deliverable.platform.toLowerCase() === 'instagram') {
      return '/src/assets/img/social/instagram.png';
    } else if (props.deliverable.platform.toLowerCase() === 'tiktok') {
      return '/src/assets/img/social/tiktok.png';
    } else if (props.deliverable.platform.toLowerCase() === 'facebook') {
      return '/src/assets/img/social/facebook.png';
    } else if (props.deliverable.platform.toLowerCase() === 'linkedin') {
      return '/src/assets/img/social/linkedin.png';
    }
    return '';
  });

  const deliverableMediaType = computed(() => `${props.deliverable.platform} ${props.deliverable.media_type}`);

  const metricsData = computed<ProfileMetric[]>(() => {
    if (!props.deliverable) return [];

    return [
      {
        icon: 'lucide:eye',
        value: formatNumber(props.deliverable.impressions, { notation: 'compact' }),
        label: 'Impressions',
        color: '#3B82F6',
      },
      {
        icon: 'lucide:users',
        value: formatNumber(props.deliverable.reach, { notation: 'compact' }),
        label: 'Reach',
        color: '#22D3EE',
      },
      {
        icon: 'lucide:download',
        value: formatNumber(Math.abs(props.deliverable.shares), { notation: 'compact' }),
        label: 'Shares',
        color: '#06B6D4',
      },
      {
        icon: 'lucide:bookmark',
        value: formatNumber(Math.abs(props.deliverable.saved), { notation: 'compact' }),
        label: 'Saved',
        color: '#F59E0B',
      },
      {
        icon: 'lucide:trending-up',
        value: `${formatNumber(props.deliverable.engagement_rate, { notation: 'compact', fractionDigits: 1 })}%`,
        label: 'Engagement',
        color: '#10B981',
      },
    ];
  });
</script>

<template>
  <div class="deliverable-performance-item flex items-center">
    <a :href="props.deliverable.submission_link" target="_blank" rel="noopener noreferrer" class="deliverable-type flex items-center gap-8">
      <img :src="deliverablePlatformIcon" :alt="props.deliverable.platform" />
      {{ deliverableMediaType }}
    </a>
    <div class="metrics-container">
      <div v-for="(metric, index) in metricsData" :key="index" class="metric-card" :class="metric.label.toLowerCase()">
        <div class="metric-value">{{ metric.value }}</div>
        <div class="metric-label">{{ metric.label }}</div>
      </div>

      <div class="metric-card cpe-card">
        <div class="cpe-value">${{ deliverable.cpe.toFixed(2) }}</div>
        <div class="metric-label">CPE</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '@/assets/styles/variables.scss';
  @import url('./style/creator-social-metrics.scss');

  .deliverable-performance-item {
    padding-block: 4px;
    width: 100%;
    justify-content: space-between;
    flex-direction: column;
    gap: 16px;
  }

  .deliverable-type {
    background: $bg-light;
    padding: 4px 16px;
    border: 2px solid $input-border;
    font-weight: 500;
    border-radius: 9999px;
    font-size: 14px;
    color: $neutral-gray;
    min-width: 170px;
    align-self: flex-start;

    img {
      width: 16px;
      height: 16px;
    }
  }

  .metrics-container {
    width: 80%;

    .metric-card {
      background-color: transparent;
      padding: 0;
      border-radius: 0;

      &:hover {
        transform: none;
        box-shadow: none;
      }

      &:nth-of-type(odd) {
        border-right: 1px solid #e6e6e6;
      }
    }

    .metric-value,
    .cpe-value {
      font-size: 14px;
      color: $neutral-black;
      font-weight: bold;
    }
  }

  @media screen and (min-width: 768px) {
    .metrics-container {
      .metric-card {
        & {
          border-right: 1px solid #e6e6e6;
        }

        &:nth-of-type(3n + 3) {
          border-right: none;
        }
      }
    }
  }

  @media screen and (min-width: 1024px) {
    .deliverable-performance-item {
      gap: 0;
      flex-direction: row;

      .deliverable-type {
        align-self: center;
      }

      .metrics-container {
        width: 100%;

        .metric-card:nth-of-type(3n + 3) {
          border-right: none;
        }
      }
    }
  }
</style>
